import { useEffect, useRef } from 'react';

import { useThreadScopedState } from 'hooks/useThreadScopedState';
import {
  ThreadIngestionStatus,
  getThreadIngestionStatus,
} from 'lib/api/getThreadIngestionStatus';
import { Maybe, Undef } from 'types/UtilityTypes';

export const LATEST_EMAIL_POLL_INTERVAL_MS = 2_000;
// Batch quotes can take tens of seconds to process, but a stuck email must not leave Kit
// loading forever. The backend status expires after 10 minutes on its own.
export const LATEST_EMAIL_MAX_WAIT_MS = 2 * 60_000;

/**
 * Whether the thread's latest email is still being processed. Calls `onProcessed` when it
 * finishes (or we stop waiting) so the caller can fetch what it produced.
 */
export function useLatestEmailProcessing(
  threadID: Maybe<string>,
  onProcessed: (threadID: string) => void
): boolean {
  const [isProcessing, setIsProcessingForThread] = useThreadScopedState(
    threadID,
    false
  );
  // Latest callback, so a new closure each render doesn't restart polling.
  const onProcessedRef = useRef(onProcessed);
  onProcessedRef.current = onProcessed;

  // Check when the thread opens and when the rep comes back to the sidebar, since a reply can
  // land while the thread is open. Poll only while the email is in flight.
  useEffect(() => {
    if (!threadID) {
      return;
    }

    let cancelled = false;
    let isPolling = false;
    let hasGivenUp = false;
    let startedAt: Maybe<number> = null;
    let timeoutID: Undef<number>;

    const finish = () => {
      isPolling = false;
      // Notify before clearing so the caller can start its refetch in the same render.
      if (startedAt !== null) {
        startedAt = null;
        onProcessedRef.current(threadID);
      }
      setIsProcessingForThread(threadID, false);
    };

    const poll = async () => {
      isPolling = true;
      const res = await getThreadIngestionStatus(threadID);
      if (cancelled) {
        return;
      }

      if (res.isErr() || res.value !== ThreadIngestionStatus.InFlight) {
        finish();
        return;
      }

      startedAt ??= Date.now();
      if (Date.now() - startedAt >= LATEST_EMAIL_MAX_WAIT_MS) {
        hasGivenUp = true;
        finish();
        return;
      }

      setIsProcessingForThread(threadID, true);
      timeoutID = window.setTimeout(poll, LATEST_EMAIL_POLL_INTERVAL_MS);
    };

    const pollIfIdle = () => {
      if (!isPolling && !hasGivenUp) {
        void poll();
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        pollIfIdle();
      }
    };

    void poll();
    window.addEventListener('focus', pollIfIdle);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutID);
      window.removeEventListener('focus', pollIfIdle);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [threadID, setIsProcessingForThread]);

  return isProcessing;
}
